import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  FiBookOpen, FiCheck, FiChevronDown, FiChevronRight, FiHelpCircle, FiInfo, FiLock, FiPlay, FiRotateCcw, FiSearch, FiX,
} from "react-icons/fi";
import { tokens } from "../../screens/activity-logs/styles";
import { Capsule, PrimaryCapsule, RoundButton } from "../../screens/creditos/components/ControlBar";
import { SANS } from "../../screens/creditos/styles";
import { tokens as T, hairline } from "../../styles/designTokens";

/**
 * Entrada do tutorial: o botão de ajuda da barra superior, com o guia da tela
 * (capítulos, progresso e glossário), e a oferta discreta do primeiro acesso.
 */

const rise = keyframes`from { opacity: 0; transform: translateY(8px) scale(0.985); } to { opacity: 1; transform: none; }`;

const Dot = styled.span`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${T.brand};
  box-shadow: 0 0 0 2px ${({ theme }) => tokens.solid(theme)};
`;

const Panel = styled.div`
  position: fixed;
  z-index: 11000;
  width: min(370px, calc(100vw - 24px));
  max-height: min(620px, calc(100vh - 90px));
  overflow-y: auto;
  padding: 16px;
  border-radius: 18px;
  font-family: ${SANS};
  color: ${({ theme }) => tokens.label(theme)};
  background: ${({ theme }) => tokens.solid(theme)};
  border: 0.5px solid ${({ theme }) => hairline(theme, true)};
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);
  animation: ${rise} 200ms ${T.easeOut} both;
  @media (prefers-reduced-motion: reduce) { animation: none; }

  .head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
  h4 { margin: 0; font-size: 1.04em; font-weight: 600; letter-spacing: -0.02em; }
  .intro { margin: 3px 0 0; font-size: 0.82em; line-height: 1.45; color: ${({ theme }) => tokens.label2(theme)}; }
  .meter { display: flex; align-items: center; gap: 8px; margin-top: 12px; font-size: 0.74em; color: ${({ theme }) => tokens.label2(theme)}; font-variant-numeric: tabular-nums; }
  .meter .track { flex: 1; height: 4px; border-radius: 2px; overflow: hidden; background: rgba(118,118,128,0.18); }
  .meter .track i { display: block; height: 100%; border-radius: 2px; background: ${T.brand}; transition: width 400ms ${T.easeOut}; }
  .label { margin: 14px 0 6px; font-size: 0.74em; font-weight: 500; color: ${({ theme }) => tokens.label2(theme)}; }
  .chap { display: flex; align-items: center; gap: 10px; width: 100%; padding: 9px 10px; border: none; border-radius: 12px; background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer; transition: background-color 160ms ease; }
  .chap:hover { background: rgba(118,118,128,0.1); }
  .chap:focus-visible { outline: 2px solid ${T.brand}; outline-offset: -2px; }
  .ico { flex-shrink: 0; display: flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 50%; font-size: 12px; background: rgba(118,118,128,0.14); color: ${({ theme }) => tokens.label2(theme)}; font-weight: 600; }
  .ico.done { color: ${T.brand}; background: ${T.brandSoft}; }
  .t { flex: 1; min-width: 0; }
  .t b { display: block; font-size: 0.88em; font-weight: 500; letter-spacing: -0.01em; }
  .t span { display: block; margin-top: 1px; font-size: 0.76em; line-height: 1.35; color: ${({ theme }) => tokens.label2(theme)}; }
  .chev { flex-shrink: 0; font-size: 14px; color: ${({ theme }) => tokens.label3(theme)}; }
  .search { display: flex; align-items: center; gap: 7px; padding: 7px 10px; border-radius: 10px; font-size: 0.82em; background: rgba(118,118,128,0.12); }
  .search svg { flex-shrink: 0; color: ${({ theme }) => tokens.label3(theme)}; }
  .search input { flex: 1; min-width: 0; border: none; outline: none; background: none; color: inherit; font: inherit; }
  .term { border-top: 0.5px solid ${({ theme }) => hairline(theme, true)}; }
  .term:first-of-type { border-top: none; }
  .term button { display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%; padding: 9px 2px; border: none; background: none; color: inherit; font: inherit; font-size: 0.84em; font-weight: 500; text-align: left; cursor: pointer; }
  .term button:focus-visible { outline: 2px solid ${T.brand}; outline-offset: -2px; border-radius: 6px; }
  .term svg { flex-shrink: 0; color: ${({ theme }) => tokens.label3(theme)}; transition: transform 180ms ease; }
  .term.open svg { transform: rotate(180deg); }
  .term p { margin: 0 2px 10px; font-size: 0.8em; font-weight: 300; line-height: 1.5; color: ${({ theme }) => tokens.label2(theme)}; }
  .empty { padding: 8px 2px; font-size: 0.8em; color: ${({ theme }) => tokens.label2(theme)}; }
  .chap:disabled { cursor: default; background: transparent; }
  .chap:disabled .t { opacity: 0.55; }
  .notice { display: flex; gap: 8px; margin-top: 12px; padding: 9px 11px; border-radius: 11px; font-size: 0.78em; line-height: 1.42; color: ${({ theme }) => tokens.label2(theme)}; background: rgba(118,118,128,0.1); }
  .notice svg { flex-shrink: 0; margin-top: 2px; }
  .reset { display: inline-flex; align-items: center; gap: 6px; margin-top: 12px; padding: 0; border: none; background: none; color: ${({ theme }) => tokens.label2(theme)}; font: inherit; font-size: 0.76em; cursor: pointer; }
  .reset:hover { color: ${({ theme }) => tokens.label(theme)}; }
`;

const Offer = styled.div`
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: 10500;
  width: min(330px, calc(100vw - 24px));
  padding: 15px 16px 13px;
  border-radius: 18px;
  font-family: ${SANS};
  color: ${({ theme }) => tokens.label(theme)};
  background: ${({ theme }) => tokens.solid(theme)};
  border: 0.5px solid ${({ theme }) => hairline(theme, true)};
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.3);
  animation: ${rise} 320ms ${T.easeOut} both;
  @media (prefers-reduced-motion: reduce) { animation: none; }

  .row { display: flex; gap: 11px; }
  .ic { flex-shrink: 0; display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 11px; font-size: 16px; color: ${T.brand}; background: ${T.brandSoft}; }
  b { display: block; font-size: 0.94em; font-weight: 600; letter-spacing: -0.015em; }
  p { margin: 3px 0 0; font-size: 0.8em; line-height: 1.45; color: ${({ theme }) => tokens.label2(theme)}; }
  .actions { display: flex; justify-content: flex-end; gap: 6px; margin-top: 12px; }
`;

export function TourOffer({ tour }) {
  const [delayed, setDelayed] = useState(false);
  useEffect(() => {
    if (!tour.showOffer) return undefined;
    const timer = setTimeout(() => setDelayed(true), 1400);
    return () => clearTimeout(timer);
  }, [tour.showOffer]);
  if (!tour.showOffer || !delayed) return null;
  return createPortal(
    <Offer role="status">
      <div className="row">
        <span className="ic"><FiPlay /></span>
        <div>
          <b>{tour.offer.title}</b>
          <p>{tour.offer.text}</p>
        </div>
      </div>
      <div className="actions">
        <Capsule type="button" onClick={tour.dismissOffer}>Agora não</Capsule>
        <PrimaryCapsule type="button" onClick={() => tour.start(tour.offerKey)}>Começar</PrimaryCapsule>
      </div>
    </Offer>,
    document.body,
  );
}

export default function TourLauncher({ tour }) {
  const { definition, progress, chapters, notice } = tour;
  const [open, setOpen] = useState(false);
  const [openTerm, setOpenTerm] = useState(null);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState({ top: 80, right: 20 });
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const toggle = () => {
    const r = buttonRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 10, right: Math.max(12, window.innerWidth - r.right) });
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const go = (key, index = 0) => { setOpen(false); tour.start(key, index); };
  const total = chapters.length;
  const doneCount = chapters.filter((c) => progress.done.includes(c.key)).length;
  // Só retoma se o capítulo fizer sentido com o que a tela mostra agora
  const resume = progress.resume && chapters.find((c) => c.key === progress.resume.chapterKey && !c.isLocked);
  const term = q.trim().toLowerCase();
  const glossary = (definition.glossary || []).filter((g) => !term || g.term.toLowerCase().includes(term) || g.text.toLowerCase().includes(term));
  const unseen = !!tour.offerKey && !progress.offersSeen.includes(tour.offerKey) && !progress.done.includes(tour.offerKey);

  return (
    <>
      <span style={{ position: "relative", display: "inline-flex" }}>
        <RoundButton ref={buttonRef} type="button" onClick={toggle} title="Guia desta tela" aria-label="Guia desta tela" aria-expanded={open} data-tour="help">
          <FiHelpCircle />
        </RoundButton>
        {unseen && <Dot aria-hidden="true" />}
      </span>

      {open && createPortal(
        <Panel ref={panelRef} role="dialog" aria-label={`Guia: ${definition.title}`} style={{ top: pos.top, right: pos.right }}>
          <div className="head">
            <div>
              <h4>{definition.title}</h4>
              <p className="intro">{definition.intro}</p>
            </div>
            <RoundButton type="button" onClick={() => setOpen(false)} aria-label="Fechar guia" style={{ width: 28, height: 28, fontSize: 13, flexShrink: 0 }}><FiX /></RoundButton>
          </div>
          <div className="meter">
            <span className="track"><i style={{ width: `${total > 0 ? (doneCount / total) * 100 : 0}%` }} /></span>
            {doneCount} de {total}
          </div>

          {resume && (
            <PrimaryCapsule type="button" onClick={() => go(resume.key, progress.resume.index)} style={{ width: "100%", justifyContent: "center", marginTop: 12 }}>
              <FiPlay /> Continuar: {resume.title}
            </PrimaryCapsule>
          )}

          {notice && <div className="notice" role="status"><FiInfo /><span>{notice}</span></div>}

          <div className="label">Tutoriais</div>
          {chapters.map((c, i) => {
            const done = progress.done.includes(c.key);
            let icon = i + 1;
            if (c.isLocked) icon = <FiLock />;
            else if (done) icon = <FiCheck />;
            return (
              <button key={c.key} type="button" className="chap" disabled={c.isLocked} onClick={() => go(c.key)}>
                <span className={`ico${done && !c.isLocked ? " done" : ""}`}>{icon}</span>
                <span className="t"><b>{c.title}</b><span>{c.summary} · {c.minutes}</span></span>
                {!c.isLocked && <FiChevronRight className="chev" />}
              </button>
            );
          })}

          {(definition.glossary || []).length > 0 && (
            <>
              <div className="label" style={{ display: "flex", alignItems: "center", gap: 6 }}><FiBookOpen /> Entenda os termos</div>
              <label className="search">
                <FiSearch />
                <input placeholder="Buscar um termo" aria-label="Buscar um termo" value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
              <div style={{ marginTop: 4 }}>
                {glossary.map((g) => {
                  const isOpen = openTerm === g.term || !!term;
                  return (
                    <div key={g.term} className={`term${isOpen ? " open" : ""}`}>
                      <button type="button" aria-expanded={isOpen} onClick={() => setOpenTerm((cur) => (cur === g.term ? null : g.term))}>
                        {g.term}<FiChevronDown />
                      </button>
                      {isOpen && <p>{g.text}</p>}
                    </div>
                  );
                })}
                {glossary.length === 0 && <div className="empty">Nenhum termo encontrado.</div>}
              </div>
            </>
          )}

          {(doneCount > 0 || progress.resume) && (
            <button type="button" className="reset" onClick={tour.reset}><FiRotateCcw /> Recomeçar o progresso</button>
          )}
        </Panel>,
        document.body,
      )}
    </>
  );
}
